import React, { useEffect, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { openTaskManagerBoardTab } from '../../store/tabsStore';
import BoardTile from './BoardTile';
import BoardsOverviewToolbar from './BoardsOverviewToolbar';
import BoardsRecentStrip from './BoardsRecentStrip';
import BoardsRowList from './BoardsRowList';
import useBoardOwners from './useBoardOwners';
import { useTaskBoardUiStore } from '../../store/taskBoardUiStore';
import { useBoardsUiStore } from '../../extensions/bundled/task-manager/boardsUiStore';
import { scopeChips, effectiveScope, boardMatchesHome, SCOPE_ALL } from '../../extensions/bundled/task-manager/boardsHomeFilter';
import {
  matchesQuick,
  quickChipCounts,
  overviewTotals,
  sortBoards,
  recentBoards,
  formatOpened,
  openedKey,
} from '../../extensions/bundled/task-manager/boardsOverview';
import { VIEW_ROWS } from '../../extensions/bundled/task-manager/boardsLocalPrefs';
import { loadBoardPins, toggleBoardPin, isBoardPinned } from '../../extensions/bundled/task-manager/boardPins';
import { LOCATION } from '../../records/itemLocation';
import OsScroll from '../ui/OsScroll';
import './BoardsHome.css';
import './BoardsOverview.css';
import LocalCapHint from '../plans/LocalCapHint';
import useBoardsProjectScope from './useBoardsProjectScope';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

/** "Personal · 36 boards" / "All · 37 boards" / "<team> · 2 boards". */
function scopeHeading(scope, chips, count) {
  const chip = (chips || []).find((c) => c.key === scope);
  const name = scope === SCOPE_ALL ? 'All' : chip?.label || 'Personal';
  return `${name} · ${fmt(count)} ${count === 1 ? 'board' : 'boards'}`;
}

/**
 * The Boards overview (list 7 of the Boards Redesign): toolbar, the recent
 * strip, a summary line and the grid of tiles (or rows) with the dashed
 * "New board" tile last. TaskBoardTab still owns the data, the modals and
 * every action, and hands them in as props under their original names.
 *
 * Filtering order: scope chip → text → quick chips; the quick-chip counts
 * are taken over the scope's boards so they do not jump while typing.
 */
export default function BoardsHome({
  boards,
  boardFilter,
  setBoardFilter,
  boardFilterRef,
  teamById,
  roleForTeam,
  shareableTeams,
  canMoveLocation,
  moveBoardLocation,
  movingBoardId,
  openEditBoardModal,
  openCreateBoardModal,
  setDeleteBoardTarget,
  setShareBoardTarget,
  setPrivateBoardTarget,
}) {
  // Scope chips (All · Personal · each team with boards here). The stored
  // scope is only applied while its chip exists; a vanished team (left it,
  // last board moved) falls back to All, and the store is reset so the old
  // team does not silently re-apply if it comes back.
  const scopeFilter = useTaskBoardUiStore((s) => s.scopeFilter);
  const setScopeFilter = useTaskBoardUiStore((s) => s.setScopeFilter);
  // Project scope (cards 3.1 / 3.2): the toolbar project decides which boards
  // show; the Personal / Team chips stay only under "All projects" (0.4).
  const project = useBoardsProjectScope();
  const allChips = useMemo(() => scopeChips(boards, teamById), [boards, teamById]);
  const chips = project.projectAll ? allChips : [];
  const scope = project.projectAll ? effectiveScope(scopeFilter, chips) : SCOPE_ALL;
  useEffect(() => {
    if (project.projectAll && scope !== scopeFilter) setScopeFilter(SCOPE_ALL);
  }, [project.projectAll, scope, scopeFilter, setScopeFilter]);

  const lastOpened = useBoardsUiStore((s) => s.lastOpened);
  const prefs = useBoardsUiStore((s) => s.overviewPrefs);
  const setPrefs = useBoardsUiStore((s) => s.setOverviewPrefs);
  const quick = useBoardsUiStore((s) => s.overviewQuick);
  const setQuick = useBoardsUiStore((s) => s.setOverviewQuick);
  const pinned = useBoardsUiStore((s) => s.pinned);
  const ownerFor = useBoardOwners(boards);
  const now = Date.now();

  // Pins are personal records (boardPins.js); re-read whenever the boards
  // reload, so a pin made on another device shows with the next sync.
  useEffect(() => {
    loadBoardPins();
  }, [boards]);
  const togglePin = (board) => {
    toggleBoardPin(board.id).catch((err) => console.warn('[task-manager] pin failed:', err));
  };

  const inScope = useMemo(
    () => (boards || []).filter((b) => project.inProject(b) && boardMatchesHome(b, { scope }, teamById)),
    [boards, scope, teamById, project]
  );
  const quickCounts = useMemo(() => quickChipCounts(inScope), [inScope]);
  const shown = useMemo(
    () =>
      sortBoards(
        inScope.filter((b) => boardMatchesHome(b, { text: boardFilter, scope }, teamById) && matchesQuick(b, quick)),
        prefs.sort,
        lastOpened,
        pinned
      ),
    [inScope, boardFilter, scope, teamById, quick, prefs.sort, lastOpened, pinned]
  );
  const totals = overviewTotals(shown);
  const recent = recentBoards(inScope, lastOpened, now);

  const openBoard = (board) => openTaskManagerBoardTab({ boardId: board.id, label: board.name });

  // One builder for a board's props, shared by the card grid and the row
  // view, so the canManage / share / move gating cannot differ between them.
  const tileProps = (b) => {
    const isTeam = b.visibility === 'team';
    // Team boards: viewers can't manage. Private boards: always yours.
    const canManage = !isTeam || roleForTeam(b.teamId) !== 'viewer';
    return {
      board: b,
      teamLabel: isTeam ? teamById.get(String(b.teamId))?.name : null,
      canManage,
      canShare: !isTeam && shareableTeams.length > 0,
      onOpen: openBoard,
      onEdit: openEditBoardModal,
      onDelete: setDeleteBoardTarget,
      onMoveLocation: isTeam || !canMoveLocation ? null : moveBoardLocation,
      moving: movingBoardId === b.id,
      openedLabel: formatOpened(lastOpened?.[openedKey(b)], now),
      owner: ownerFor(b),
      pinned: isBoardPinned(pinned, b.id),
      onTogglePin: togglePin,
      projectMenu: project.menuFor(b),
      // Editors see it; the server allows the team's admins and whoever
      // shared the board, and says so otherwise.
      onMakePrivate: isTeam && canManage && setPrivateBoardTarget
        ? (board) => setPrivateBoardTarget({ id: board.id, name: board.name, teamId: String(board.teamId), taskCount: board.taskCount })
        : null,
      onShare: (board) =>
        setShareBoardTarget({
          id: board.id,
          name: board.name,
          teamId: shareableTeams[0]?.id != null ? String(shareableTeams[0].id) : '',
          // Kept on this device only: goes to the cloud first.
          onDevice: board.location === LOCATION.DEVICE,
        }),
    };
  };
  const items = shown.map((b) => ({ key: `${b.scopeKey || 'personal'}:${b.id}`, props: tileProps(b) }));

  // Only a FILTER can produce "nothing to show": with no boards at all the
  // grid still renders, holding just the create tile, so an empty Boards home
  // looks like the populated one minus the boards (owner request 07.09.2026).
  // A project with no boards yet is not a filter miss: the grid (with its
  // create tile) stays, with a line saying so.
  const projectEmpty = !project.projectAll && inScope.length === 0;
  const noMatch = (boards || []).length > 0 && shown.length === 0 && !projectEmpty;

  // OsScroll (OverlayScrollbars) like every other scroll area in the app — a
  // plain overflow here drew Windows' native bar with its arrow buttons
  // (owner, 30.09.2026). The toolbar is always rendered, even with zero
  // boards: the home keeps one stable shape (owner, 07.09.2026).
  return (
    <OsScroll className="task-board-home">
      <BoardsOverviewToolbar
        boardFilter={boardFilter}
        setBoardFilter={setBoardFilter}
        boardFilterRef={boardFilterRef}
        chips={chips}
        scope={scope}
        onScope={setScopeFilter}
        quick={quick}
        quickCounts={quickCounts}
        onQuick={setQuick}
        sort={prefs.sort}
        onSort={(sort) => setPrefs({ sort })}
        view={prefs.view}
        onView={(view) => setPrefs({ view })}
        onNewBoard={openCreateBoardModal}
      />
      <div className="boards-body">
        <BoardsRecentStrip boards={recent} lastOpened={lastOpened} now={now} onOpen={openBoard} />
        {noMatch ? (
          <div className="task-board-empty">No boards match your filter.</div>
        ) : (
          <section className="boards-section" aria-label="All boards">
            <div className="boards-section-head">
              <span className="boards-section-title">{project.heading(totals.boards, scopeHeading(scope, chips, totals.boards))}</span>
              <span className="boards-section-note">
                {fmt(totals.tasks)} {totals.tasks === 1 ? 'task' : 'tasks'} · {fmt(totals.done)} done · {fmt(totals.overdue)} overdue
              </span>
            </div>
            {projectEmpty && (
              <p className="boards-project-empty">
                {project.activeName
                  ? `No boards in ${project.activeName} yet. New boards land here; add existing ones from a board's ⋯ menu under All projects.`
                  : 'Every board is in a project.'}
              </p>
            )}
            {prefs.view === VIEW_ROWS ? <BoardsRowList items={items} /> : null}
            <div className={`task-board-grid boards-grid${prefs.view === VIEW_ROWS ? ' boards-grid--rows' : ''}`}>
              {prefs.view === VIEW_ROWS ? null : items.map(({ key, props }) => <BoardTile key={key} {...props} />)}
              {/* Create tile, last so it never pushes the existing boards'
                  positions around as they are filtered. With zero boards it
                  is the whole grid. */}
              <button type="button" className="boards-new-tile" onClick={openCreateBoardModal}>
                <span className="boards-new-tile-icon" aria-hidden>
                  <Plus size={15} />
                </span>
                New board
              </button>
              {/* Plans v2 card 3.3: "3 / 1" once the cap is reached; the tile
                  still opens the upgrade on click. */}
              <LocalCapHint kind="boards" style={{ alignSelf: 'flex-start' }} />
            </div>
          </section>
        )}
      </div>
    </OsScroll>
  );
}
